import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { COLOR_SELECT_ROW_GANT, HEIGHT_ITEM } from '../../../const';
import useAccessForItem from '../../../hooks/useAccessForItem';
import { useStatusEditProjects } from '../../../hooks/useStatusEditProjects';
import { RootState } from '../../../store';
import { DragAndDropType } from '../../../types';
import { DragStrategy } from '../drag/dragStrategy';
import { AppendItem } from '../hook/useAppendChildItem';
import { useProjectsTreeContext } from '../hook/useProjectsTreeContext';
import { ProjectTreeRecordType } from '../projectsTreeTypes';
import { ProjectsTreeCellAddItem } from './projectsTreeCellAddItem';
import { ProjectsTreeExpandIcon } from './projectsTreeExpandIcon';

export interface ProjectItemCellProps {
    value?: any;
    dragType?: DragAndDropType;
    record: ProjectTreeRecordType;
    hasCollapseControl?: boolean;
    children?: React.ReactNode;
    paddingRight?: number;
    isLastColumn?: boolean;
}

const SCell = styled.div<{
    $paddingLeft: number;
    $isSelected: boolean;
    $paddingRight?: number;
    $hasRightBorder?: boolean;
}>`
    box-sizing: border-box;
    align-items: center;
    display: flex;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: ${({ $paddingLeft }) => $paddingLeft}px;
    padding-right: ${({ $paddingRight }) => $paddingRight ?? 0}px;

    height: ${HEIGHT_ITEM}px;
    border-right: ${({ $hasRightBorder }) =>
        $hasRightBorder ? `1px solid rgba(197, 197, 197, 0.5) !important` : '1px solid transparent'};
    border-bottom: 1px solid rgba(197, 197, 197, 0.5);
    border-left: 1px solid transparent;
    border-top: 1px solid transparent;
    background-color: ${({ $isSelected }) => ($isSelected ? '#fff5f0' : 'white')};
    cursor: pointer;
    position: relative;

    &.${DragStrategy.CSS_CLASS.dragOverUp} {
        border-top: 1px solid ${COLOR_SELECT_ROW_GANT};
    }

    &.${AppendItem.CSS_CLASS.appendMarker}, &.${DragStrategy.CSS_CLASS.dragOverDown} {
        border-bottom: 1px solid ${COLOR_SELECT_ROW_GANT};
    }

    &.${DragStrategy.CSS_CLASS.itemBeingDragged} {
        opacity: 0.3;
    }

    &.${AppendItem.CSS_CLASS.borderTopBottom}, &.${DragStrategy.CSS_CLASS.borderTopBottom} {
        border-top: 1px solid ${COLOR_SELECT_ROW_GANT};
        border-bottom: 1px solid ${COLOR_SELECT_ROW_GANT};
    }

    &.${AppendItem.CSS_CLASS.borderLeft}, &.${DragStrategy.CSS_CLASS.borderLeft} {
        border-left: 1px solid ${COLOR_SELECT_ROW_GANT};
    }

    &.${AppendItem.CSS_CLASS.borderRight}, &.${DragStrategy.CSS_CLASS.borderRight} {
        border-right: 1px solid ${COLOR_SELECT_ROW_GANT};
    }
`;

const ItemLevelText = styled.div<{ $level: number; $access: boolean }>`
    font-size: ${({ $level }) => ($level === 0 ? 24 : 13)}px;
    font-weight: ${({ $level }) => ($level < 2 ? 700 : undefined)};
    min-width: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    pointer-events: none;
    flex: 1;
    color: ${({ $access }) => ($access ? 'black' : '#bbb')};
`;

export const ProjectsTreeCell: React.FC<ProjectItemCellProps> = ({
    value,
    record,
    children,
    hasCollapseControl,
    dragType = 'none',
    paddingRight = 10,
    isLastColumn = false,
}) => {
    const currentGantItemId = useSelector<RootState, string | null | undefined>(
        (state) => state.gantItems.currentGantItem?.id,
    );

    const [isMouseOver, setIsMouseOver] = useState(false);

    const editProjectStatuses = useStatusEditProjects();

    const { sharedCellProps, registerCell, unregisterCell } = useProjectsTreeContext();

    const isEditableProject = editProjectStatuses.has(record.item.projectId!);

    const { onDragStart, ...cellProps } = sharedCellProps;
    const dataProps = {
        [DragStrategy.DATA_ITEM.dataItemId]: record.item.id,
        [DragStrategy.DATA_ITEM.dataItemProjectId]: record.item.projectId,
        [DragStrategy.DATA_ITEM.dataDragType]: dragType,
    };

    const { onMouseEnter, onMouseLeave } = cellProps;

    const ref = useRef<HTMLDivElement>(null);
    const hasChildren = record.item.children && record.item.children.length > 0;
    const noCollapsePadding = hasChildren ? 10 : 35;

    const paddingLeft = hasCollapseControl ? (record.level - 1) * 10 + noCollapsePadding : 10;
    useEffect(() => {
        registerCell(record.item.id, ref);
        return () => unregisterCell(record.item.id, ref);
    }, [record.item.id, ref]);

    const accessView = useAccessForItem(record.item, ['work-item.all.view', 'work-item.owner', 'work-item.assignee']);
    const accessDrag = useAccessForItem(record.item, [
        'work-item.all.edit',
        'work-item.all.manage',
        'work-item.owner.edit',
        'work-item.owner.manage',
        'work-item.assignee.edit',
        'work-item.assignee.manage',
    ]);

    const accessAdd = useAccessForItem(record.item, [
        'work-item.all.create',
        'work-item.all.edit',
        'work-item.all.manage',
        'work-item.owner.create',
        'work-item.owner.edit',
        'work-item.owner.manage',
        'work-item.assignee.create',
        'work-item.assignee.edit',
        'work-item.assignee.manage',
    ]);

    return (
        <div>
            <SCell
                className="project-item"
                draggable={accessDrag && isEditableProject && dragType !== 'none'}
                ref={ref}
                $isSelected={accessView && currentGantItemId === record.item.id}
                $paddingLeft={paddingLeft}
                $paddingRight={paddingRight}
                {...(accessView ? cellProps : {})}
                {...dataProps}
                onDragStart={accessView && dragType !== 'none' ? onDragStart : undefined}
                onMouseEnter={
                    accessView
                        ? (e: React.MouseEvent<HTMLDivElement>) => {
                              onMouseEnter(e);
                              setIsMouseOver(true);
                          }
                        : undefined
                }
                onMouseLeave={
                    accessView
                        ? (e: React.MouseEvent<HTMLDivElement>) => {
                              onMouseLeave(e);
                              setIsMouseOver(false);
                          }
                        : undefined
                }
            >
                {hasCollapseControl && hasChildren && <ProjectsTreeExpandIcon item={record.item} />}

                {children ? (
                    children
                ) : (
                    <ItemLevelText $level={record.level} $access={accessView}>
                        {value}
                    </ItemLevelText>
                )}
                {accessAdd && isEditableProject && isMouseOver && isLastColumn && record.level > 0 && (
                    <ProjectsTreeCellAddItem item={record.item} />
                )}
            </SCell>
        </div>
    );
};
